import { useQuery } from "@tanstack/react-query";
import { WebhookService } from "../../../services/api";
import {
  Box,
  Dialog,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { prepareFilterString } from "../../../helpers/filters";
import FormTemplate from "../../templates/FormTemplate";
import FieldLabel from "../../atoms/FieldLabel";
import { WebhookLog } from "../../../services/swagger";

interface WebhookLogsProps {
  webhookId: string;
}

const limit = 10;

export default function WebhookLogs(props: WebhookLogsProps) {
  const { t } = useTranslation("WebhookLogs");
  const [page, setPage] = useState(1);
  const [webhookLog, setWebhookLog] = useState<WebhookLog | null>(null);

  const { data } = useQuery({
    queryKey: ["webhookLogs", { webhookId: props.webhookId, page }],
    queryFn: async () => {
      const res = await WebhookService.getWebhookLogList({
        limit,
        search: "",
        skip: (page - 1) * limit,
        filters: prepareFilterString([
          {
            field: "ref",
            type: "id",
            operator: "eq",
            value: props.webhookId,
          },
        ]),
        sortDirection: "desc",
        sortField: "date",
      });
      return res.data.data;
    },
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const handleRowClick = (log: WebhookLog) => {
    setWebhookLog(log);
  };

  const handleCloseDialog = () => {
    setWebhookLog(null);
  };

  return (
    <Stack spacing={2} maxWidth={700}>
      <Dialog
        open={!!webhookLog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {webhookLog && (
          <FormTemplate
            title={t("Payload")}
            disableSubmitButton
            disableSubmit
            onCancel={handleCloseDialog}
          >
            <Stack spacing={2}>
              <Box>
                <FieldLabel>{t("Date")}</FieldLabel>
                <Typography>
                  {new Date(webhookLog?.date).toLocaleString("tr")}
                </Typography>
              </Box>
              <Box>
                <FieldLabel>{t("Url")}</FieldLabel>
                <Typography>{webhookLog?.url}</Typography>
              </Box>
              <Box>
                <FieldLabel>{t("ResponseCode")}</FieldLabel>
                <Typography>
                  {webhookLog?.status} {webhookLog?.statusText}
                </Typography>
              </Box>
              <Box>
                <FieldLabel>{t("Response")}</FieldLabel>
                <Typography>
                  {JSON.stringify(webhookLog?.response, null, 2)}
                </Typography>
              </Box>
            </Stack>
          </FormTemplate>
        )}
      </Dialog>
      <FieldLabel>{t("WebhookLogs")}</FieldLabel>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Date")}</TableCell>
                <TableCell>{t("Url")}</TableCell>
                <TableCell>{t("ResponseCode")}</TableCell>
                <TableCell>{t("Status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.docs.map((log) => (
                <TableRow
                  key={log._id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(log)}
                >
                  <TableCell>
                    {new Date(log.date).toLocaleString("tr")}
                  </TableCell>
                  <TableCell>{log.url}</TableCell>
                  <TableCell>{log.status}</TableCell>
                  <TableCell>{log.statusText}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          shape="rounded"
          color="primary"
          sx={{ p: 1 }}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          count={Math.ceil((data?.count || 0) / limit)}
        />
      </Paper>
    </Stack>
  );
}
